/**
 * Send Feedback — bug reports and feature requests. A single form with a
 * Bug/Feature toggle, one free-text Description, and attachments. A bug may
 * optionally name an affected endpoint; when it does, the report anchors to
 * that endpoint (server attaches its metadata + a telemetry bundle), otherwise
 * it is filed as a product/dashboard bug. Attachment upload (pre-signed PUT)
 * and the three submit flows are routed through reportClient.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TabStrip from "../components/TabStrip.jsx";
import Select from "../components/Select.jsx";
import EndpointPicker from "../components/EndpointPicker.jsx";
import { EYEBROW_CLASS, NOTE_CLASS } from "../lib/ui.js";
import { parseServerTimestamp } from "../lib/traceUtils.js";
import {
    AttachmentPurpose,
    createAttachmentUploadUrl,
    createBugReport,
    createProductBugReport,
    createFeatureRequest,
    getEndpointMetadata,
} from "../lib/reportClient.js";

// Canonical report-type keys. Reference REPORT_TYPE.* at every
// comparison/branch site instead of bare string literals so a typo is a
// reference error rather than a silent fallback.
const REPORT_TYPE = Object.freeze({
    BUG:     "bug",
    FEATURE: "feature",
});

const REPORT_TYPES = {
    [REPORT_TYPE.BUG]:     { label: "Bug",     submitLabel: "Send bug report" },
    [REPORT_TYPE.FEATURE]: { label: "Feature", submitLabel: "Send feature request" },
};

// Options for the Type dropdown, derived from REPORT_TYPES so label text has a
// single source.
const TYPE_OPTIONS = Object.entries(REPORT_TYPES).map(([value, config]) => ({ value, label: config.label }));

// The three wire submissions the form fans out to, derived from the toggle
// plus whether an endpoint was picked. A bug with a selected endpoint anchors
// to it — the server attaches that endpoint's metadata and a telemetry bundle
// (endpoint-bug); a bug without one is a product/dashboard bug; a feature
// request is always endpoint-less. Pure so the routing decision is unit
// testable and so the attachment-upload purpose and the submit call can't
// disagree about which surface a submission is.
const SUBMISSION_ROUTE = Object.freeze({
    ENDPOINT_BUG: "endpoint-bug",
    PRODUCT_BUG:  "product-bug",
    FEATURE:      "feature",
});
export function submissionRouteFor(reportType, hasEndpoint) {
    if (reportType === REPORT_TYPE.FEATURE) return SUBMISSION_ROUTE.FEATURE;
    return hasEndpoint ? SUBMISSION_ROUTE.ENDPOINT_BUG : SUBMISSION_ROUTE.PRODUCT_BUG;
}
const ROUTE_ATTACHMENT_PURPOSE = {
    [SUBMISSION_ROUTE.ENDPOINT_BUG]: AttachmentPurpose.BUG_REPORT,
    [SUBMISSION_ROUTE.PRODUCT_BUG]:  AttachmentPurpose.PRODUCT_BUG_REPORT,
    [SUBMISSION_ROUTE.FEATURE]:      AttachmentPurpose.FEATURE_REQUEST,
};

const MAX_ATTACHMENT_COUNT = 10;
const MAX_ATTACHMENT_BYTES = 250 * 1024 * 1024; // 250 MiB — keep in sync with api-server ISSUE_ATTACHMENT_MAX_SIZE_BYTES (O-1648)
// Per-file upload timeout scales with size so a large attachment on a slow
// link doesn't trip the client timeout mid-PUT (O-1648): a 5 min floor plus a
// budget assuming only ~256 KiB/s (≈2 Mbps) sustained throughput. Capped at
// the presigned-URL TTL (3600s) since the PUT can't outlive the URL anyway.
const UPLOAD_TIMEOUT_FLOOR_MS = 5 * 60 * 1000;
const UPLOAD_TIMEOUT_THROUGHPUT_BPS = 256 * 1024;
const UPLOAD_TIMEOUT_CAP_MS = 3600 * 1000;
function uploadTimeoutMs(sizeBytes) {
    const budget = ((Number(sizeBytes) || 0) / UPLOAD_TIMEOUT_THROUGHPUT_BPS) * 1000;
    return Math.min(UPLOAD_TIMEOUT_CAP_MS, UPLOAD_TIMEOUT_FLOOR_MS + budget);
}
const BLOCKED_EXTENSIONS = new Set([
    "exe", "dll", "msi", "dmg", "pkg", "sh", "bat", "cmd", "ps1", "scr", "com", "jar", "app",
]);

// Extension → content type, used only to *infer* a sane Content-Type for the
// presigned PUT when the browser doesn't report one. There is deliberately no
// content-type allow-list: client-reported MIME types are untrusted (and in
// prod the upload-time check is advisory anyway — CloudFront signed URLs don't
// enforce headers), and the allow-list was false-rejecting legitimate uploads
// such as a .log the OS maps to application/octet-stream (O-1865). Serve-time
// defense for downloaded attachments (Content-Disposition / nosniff / edge WAF)
// is tracked in O-445.
const EXTENSION_CONTENT_TYPES = {
    csv: "text/csv", gif: "image/gif", gz: "application/gzip", heic: "image/heic", heif: "image/heif",
    jpeg: "image/jpeg", jpg: "image/jpeg", json: "application/json", log: "text/plain", md: "text/markdown",
    pdf: "application/pdf", png: "image/png", tar: "application/x-tar", text: "text/plain", txt: "text/plain",
    webp: "image/webp", zip: "application/zip",
};

// Read ?reportType= and resolve it to a known REPORT_TYPE key. The prior form
// carried three types (endpoint-bug / product-bug / feature) plus a legacy
// `bug` alias; the two bug variants now fold into a single Bug toggle, so any
// bug-ish value maps to BUG and only `feature` maps to FEATURE. Unknown values
// (typo, stale link) fall back to BUG after an explicit check rather than a
// silent map miss.
function reportTypeFromLocation() {
    if (typeof window === "undefined") return REPORT_TYPE.BUG;
    const type = new URLSearchParams(window.location.search).get("reportType");
    if (type === REPORT_TYPE.FEATURE) return REPORT_TYPE.FEATURE;
    return REPORT_TYPE.BUG;
}

// Mirror the active report type into ?reportType= so a refresh or copied
// deep-link reopens the same tab (O-725). replaceState (not pushState)
// keeps the change off the back-button history, matching how the rest of
// the dashboard syncs view params.
function syncReportTypeToLocation(type) {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("reportType") === type) return;
    url.searchParams.set("reportType", type);
    window.history.replaceState(null, "", url);
}

const INITIAL_FORM = { title: "", description: "", affectedEndpointId: "" };

const PLACEHOLDERS = {
    [REPORT_TYPE.BUG]: {
        title: "Example: Crash when opening endpoint details",
        description:
            "Describe the bug: what you were doing, what happened, and what you expected instead. " +
            "Include anything that can help us reproduce it, such as steps, timing, and the affected page. " +
            "Screenshots or logs can go in Attachments.",
    },
    [REPORT_TYPE.FEATURE]: {
        title: "Example: Make endpoint activity easier to scan",
        description:
            "Describe what you'd like to be able to do and why. What problem would it solve, " +
            "and how would you use it?",
    },
};

function trimValue(value) { return String(value || "").trim(); }
function fieldLength(value) { return trimValue(value).length; }

function getExtension(filename = "") {
    const cleaned = String(filename).trim();
    const idx = cleaned.lastIndexOf(".");
    if (idx < 0 || idx === cleaned.length - 1) return "";
    return cleaned.slice(idx + 1).toLowerCase();
}
function normalizeContentType(contentType = "") {
    return String(contentType).split(";")[0].trim().toLowerCase();
}
export function inferContentType(file) {
    const explicit = normalizeContentType(file?.type);
    if (explicit) return explicit;
    return EXTENSION_CONTENT_TYPES[getExtension(file?.name)] || "application/octet-stream";
}
// Stable identity for a selected File, used to cache its uploaded object
// key across submit retries. lastModified disambiguates two distinct files
// that happen to share a name and size.
function fileIdentity(file) {
    return `${file?.name}-${file?.size}-${file?.lastModified ?? ""}`;
}
function formatBytes(bytes) {
    if (!Number.isFinite(bytes)) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function unwrapEndpointMetadata(response) {
    const wrapper = response?.metadata ?? response;
    return wrapper?.metadata ?? wrapper ?? null;
}
function metadataValue(metadata, camelKey, snakeKey = camelKey) {
    return metadata?.[camelKey] ?? metadata?.[snakeKey] ?? "";
}
function formatPlatform(platform) {
    if (platform == null || platform === "") return "Not available";
    const numericDisplay = { 1: "Windows", 2: "macOS", 3: "Linux" };
    if (numericDisplay[platform]) return numericDisplay[platform];
    const value = String(platform).replace(/^PLATFORM_/i, "");
    const display = { MACOS: "macOS", WINDOWS: "Windows", LINUX: "Linux" };
    return display[value] ?? (value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "Not available");
}
function formatTimestamp(value) {
    if (!value) return "Not available";
    try {
        const timestamp = typeof value === "object" && value.seconds != null
            ? new Date(Number(value.seconds) * 1000 + Math.floor(Number(value.nanos || 0) / 1_000_000))
            : new Date(parseServerTimestamp(value));
        return Number.isNaN(timestamp.getTime()) ? "Not available" : timestamp.toLocaleString();
    } catch {
        return "Not available";
    }
}
function endpointMetadataSummary(metadata) {
    return {
        agentVersion: metadataValue(metadata, "agentVersion", "agent_version") || "Not available",
        platform: formatPlatform(metadataValue(metadata, "platform", "operating_system")),
        observedAt: formatTimestamp(metadataValue(metadata, "snapshotTime", "snapshot_time")),
    };
}

export function errorMessage(error) {
    if (!error) return "";
    // Prefer the gateway's human message (restClient sets `serverMessage`) over
    // the raw "METHOD /path STATUS: {json}" envelope in `.message`, which leaks
    // request shape and internal detail to the person filing feedback.
    if (error.serverMessage) return error.serverMessage;
    if (error.details && typeof error.details === "string") return error.details;
    if (error.message) return error.message;
    return "Request failed";
}

// True if `value` contains a disallowed control character. C0 controls
// (0x00-0x1f) and DEL (0x7f) are rejected; in multiline fields tab (0x09),
// LF (0x0a), and CR (0x0d) are permitted. Implemented by char code rather
// than a regex literal so the source carries no raw control bytes.
function hasDisallowedControlCharacter(value, allowMultiline) {
    const str = String(value);
    for (let i = 0; i < str.length; i += 1) {
        const code = str.charCodeAt(i);
        if (code === 0x7f) return true;
        if (code <= 0x1f) {
            if (allowMultiline && (code === 0x09 || code === 0x0a || code === 0x0d)) continue;
            return true;
        }
    }
    return false;
}
function validateText(errors, field, value, label, min, max, { allowMultiline = false } = {}) {
    const length = fieldLength(value);
    if (length < min) errors[field] = `${label} must be at least ${min} characters.`;
    else if (length > max) errors[field] = `${label} must be at most ${max} characters.`;
    else if (hasDisallowedControlCharacter(value, allowMultiline)) errors[field] = `${label} cannot contain control characters.`;
}
export function validateAttachments(files) {
    const errors = [];
    if (files.length > MAX_ATTACHMENT_COUNT) errors.push(`Attach at most ${MAX_ATTACHMENT_COUNT} files.`);
    files.forEach((file) => {
        const name = file?.name || "";
        const extension = getExtension(name);
        if (!name.trim()) errors.push("Every attachment needs a filename.");
        else if (name.includes("/") || name.includes("\\") || name.trim() === "." || name.trim() === ".." || hasDisallowedControlCharacter(name, false))
            errors.push(`${name} is not a valid attachment filename.`);
        else if (BLOCKED_EXTENSIONS.has(extension)) errors.push(`${name} has a blocked file extension.`);
        else if (!file.size || file.size <= 0) errors.push(`${name} is empty.`);
        else if (file.size > MAX_ATTACHMENT_BYTES) errors.push(`${name} is larger than ${formatBytes(MAX_ATTACHMENT_BYTES)}.`);
    });
    return errors;
}

// The attachments field error for a file set, or "" when the set is valid.
// Shared by submit-time validation and the add/remove handlers so a rejected
// file's error appears the moment it is added and clears the moment it is
// removed — the user is never left in a stuck form.
export function attachmentSetError(files) {
    return validateAttachments(files).join(" ");
}

function putFile(uploadUrl, file, contentType, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        let settled = false;
        const settle = (callback, value) => { if (settled) return; settled = true; callback(value); };
        xhr.open("PUT", uploadUrl);
        xhr.timeout = uploadTimeoutMs(file?.size);
        xhr.setRequestHeader("Content-Type", contentType);
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && typeof onProgress === "function") {
                onProgress(Math.round((event.loaded / event.total) * 100));
            }
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) { onProgress?.(100); settle(resolve); }
            else settle(reject, new Error(`Upload failed with HTTP ${xhr.status}`));
        };
        xhr.onerror = () => settle(reject, new Error("Upload failed"));
        xhr.ontimeout = () => settle(reject, new Error("Upload timed out. Try again."));
        xhr.onabort = () => settle(reject, new Error("Upload canceled."));
        xhr.send(file);
    });
}

// ── Field primitives (match origin's admin inputs) ────────────────
// Same control shape as ProvisioningTokensPage: min-h-[30px], 9px inset,
// transparent fill, --line border (focus → --line-strong), grid gap-[4px]
// label using NOTE_CLASS. Error swaps the border to red.
const INPUT_BORDER = "border-[color:var(--line)] focus:border-[color:var(--line-strong)]";
const INPUT_BORDER_ERROR = "border-red-400/60";
const INPUT_CLASS =
    "min-h-[30px] w-full rounded-[2px] border bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]";
const TEXTAREA_CLASS =
    "min-h-[92px] w-full resize-y rounded-[2px] border bg-transparent px-[9px] py-[6px] text-[12px] leading-[1.4] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]";

function FieldError({ value }) {
    if (!value) return null;
    return <div className="text-[11px] leading-[1.18] text-red-400">{value}</div>;
}

function TextInput({ label, error, className = "", ...props }) {
    return (
        <label className={`grid gap-[4px] ${className}`}>
            <span className={NOTE_CLASS}>{label}</span>
            <input {...props} className={`${INPUT_CLASS} ${error ? INPUT_BORDER_ERROR : INPUT_BORDER}`} />
            <FieldError value={error} />
        </label>
    );
}

function TextArea({ label, error, className = "", ...props }) {
    return (
        <label className={`grid gap-[4px] ${className}`}>
            <span className={NOTE_CLASS}>{label}</span>
            <textarea {...props} className={`${TEXTAREA_CLASS} ${error ? INPUT_BORDER_ERROR : INPUT_BORDER}`} />
            <FieldError value={error} />
        </label>
    );
}

// Selected-attachment list. Every row — valid or rejected — carries a remove
// control, so a wrong file never locks the form and the user's write-up is
// never at risk. A file that fails validation on its own (blocked extension,
// empty, oversized) gets a red border; the specific reason renders in the
// shared attachments error below the dropzone.
export function AttachmentList({ files, statuses, onRemove }) {
    if (!files || files.length === 0) return null;
    return (
        <div className="grid grid-cols-1 gap-[6px]">
            {files.map((file, index) => {
                const key = `${file.name}-${file.size}-${index}`;
                const status = statuses?.[key];
                const rejected = validateAttachments([file]).length > 0;
                return (
                    <div
                        key={key}
                        className={`grid grid-cols-1 gap-[6px] rounded-[2px] border px-[9px] py-[7px] ${rejected ? "border-red-400/60" : "border-[color:var(--line-soft)]"}`}
                    >
                        <div className="flex items-center justify-between gap-[8px] text-[11px] leading-[1.18]">
                            <span className="min-w-0 truncate text-[color:var(--ink)]">{file.name}</span>
                            <div className="flex shrink-0 items-center gap-[8px]">
                                <span className="text-[color:var(--muted)]">{formatBytes(file.size)}</span>
                                <button
                                    type="button"
                                    onClick={() => onRemove(index)}
                                    title="Remove attachment"
                                    aria-label={`Remove ${file.name}`}
                                    className="inline-flex h-[16px] w-[16px] items-center justify-center rounded-[2px] text-[color:var(--muted-soft)] hover:bg-[color:var(--line-soft)] hover:text-[color:var(--ink)] cursor-pointer"
                                >
                                    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="h-[9px] w-[9px]">
                                        <path d="M3 3l10 10M13 3L3 13" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        {!rejected && status ? (
                            <div className="grid gap-[3px]">
                                <div className="flex items-center justify-between text-[10px] leading-[1.08] text-[color:var(--muted)]">
                                    <span>{status.label}</span>
                                    <span>{status.progress}%</span>
                                </div>
                                <div className="h-[3px] overflow-hidden rounded-[2px] bg-[color:var(--line-soft)]">
                                    <div
                                        className="h-full bg-[color:var(--green)] transition-all"
                                        style={{ width: `${Math.max(0, Math.min(100, status.progress))}%` }}
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────
export default function ReportPage() {
    const [reportType, setReportType] = useState(reportTypeFromLocation);
    const [form, setForm] = useState(INITIAL_FORM);
    const [attachments, setAttachments] = useState([]);
    const [attachmentStatuses, setAttachmentStatuses] = useState({});
    // Object keys for files already uploaded, keyed by stable per-file
    // identity (name+size+lastModified). Lets a submit retry skip files
    // that already landed in S3 instead of re-uploading every one — the
    // worst case being large log/gz bundles (O-727).
    const [uploadedObjectKeys, setUploadedObjectKeys] = useState({});
    const [selectedEndpointMetadata, setSelectedEndpointMetadata] = useState(null);
    const [loadingEndpointMetadata, setLoadingEndpointMetadata] = useState(false);
    const [endpointMetadataError, setEndpointMetadataError] = useState("");
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState("");
    const [success, setSuccess] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    const selectedType = REPORT_TYPES[reportType] || REPORT_TYPES[REPORT_TYPE.BUG];
    const isBug = reportType === REPORT_TYPE.BUG;
    const selectedFiles = useMemo(() => Array.from(attachments), [attachments]);
    const selectedEndpointId = trimValue(form.affectedEndpointId);
    // Only a bug can name an endpoint; a feature request never does.
    const hasEndpoint = isBug && Boolean(selectedEndpointId);
    const route = submissionRouteFor(reportType, hasEndpoint);
    const metadataSummary = useMemo(() => endpointMetadataSummary(selectedEndpointMetadata), [selectedEndpointMetadata]);

    // Commit a new attachment set (from an add or a remove). Prunes the upload
    // cache to files that survive so a submit retry never re-PUTs a large
    // .log/.gz bundle that already landed; resets the index-keyed status map
    // (indices shift when the set changes); and re-runs attachment validation
    // so a rejected file's error appears on add and clears on remove. Title and
    // description are untouched — recovering from a bad file never costs the
    // user their write-up.
    const commitAttachments = useCallback((nextFiles) => {
        const liveIdentities = new Set(nextFiles.map(fileIdentity));
        setUploadedObjectKeys((cache) => {
            const next = {};
            let changed = false;
            for (const [identity, objectKey] of Object.entries(cache)) {
                if (liveIdentities.has(identity)) next[identity] = objectKey;
                else changed = true;
            }
            return changed ? next : cache;
        });
        setAttachmentStatuses({});
        const attachmentError = attachmentSetError(nextFiles);
        setErrors((current) => {
            const next = { ...current };
            if (attachmentError) next.attachments = attachmentError;
            else delete next.attachments;
            return next;
        });
        setAttachments(nextFiles);
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, []);

    const appendAttachments = useCallback((files) => {
        const incoming = Array.from(files || []);
        if (incoming.length === 0) return;
        commitAttachments([...attachments, ...incoming]);
    }, [attachments, commitAttachments]);

    const removeAttachment = useCallback((index) => {
        commitAttachments(attachments.filter((_, i) => i !== index));
    }, [attachments, commitAttachments]);

    const updateField = useCallback((field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
        setErrors((current) => {
            if (!current[field]) return current;
            const next = { ...current };
            delete next[field];
            return next;
        });
    }, []);

    useEffect(() => {
        if (!hasEndpoint) {
            setSelectedEndpointMetadata(null);
            setEndpointMetadataError("");
            setLoadingEndpointMetadata(false);
            return undefined;
        }
        let cancelled = false;
        setLoadingEndpointMetadata(true);
        setEndpointMetadataError("");
        getEndpointMetadata(selectedEndpointId)
            .then((response) => { if (!cancelled) setSelectedEndpointMetadata(unwrapEndpointMetadata(response)); })
            .catch((error) => {
                if (!cancelled) { setSelectedEndpointMetadata(null); setEndpointMetadataError(errorMessage(error)); }
            })
            .finally(() => { if (!cancelled) setLoadingEndpointMetadata(false); });
        return () => { cancelled = true; };
    }, [hasEndpoint, selectedEndpointId]);

    const validateForm = useCallback(() => {
        const nextErrors = {};
        validateText(nextErrors, "title", form.title, "Title", 5, 200);
        validateText(nextErrors, "description", form.description, "Description", 10, 5000, { allowMultiline: true });
        const attachmentError = attachmentSetError(selectedFiles);
        if (attachmentError) nextErrors.attachments = attachmentError;
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }, [form, selectedFiles]);

    const uploadAttachment = useCallback(async (file, index) => {
        const identity = fileIdentity(file);
        // Idempotent: a prior submit may have already uploaded this exact
        // file. Reuse the stored object key instead of re-PUTting (O-727).
        const cachedKey = uploadedObjectKeys[identity];
        const statusKey = `${file.name}-${file.size}-${index}`;
        if (cachedKey) {
            setAttachmentStatuses((current) => ({ ...current, [statusKey]: { label: "Uploaded", progress: 100 } }));
            return cachedKey;
        }
        const contentType = inferContentType(file);
        setAttachmentStatuses((current) => ({ ...current, [statusKey]: { label: "Preparing", progress: 0 } }));
        const upload = await createAttachmentUploadUrl({
            purpose: ROUTE_ATTACHMENT_PURPOSE[route], filename: file.name, contentType, sizeBytes: file.size,
        });
        const uploadUrl = upload?.upload_url || upload?.uploadUrl;
        const objectKey = upload?.object_key || upload?.objectKey;
        const signedContentType = upload?.content_type || upload?.contentType || contentType;
        if (!uploadUrl || !objectKey) throw new Error("Attachment upload URL response was incomplete.");
        setAttachmentStatuses((current) => ({ ...current, [statusKey]: { label: "Uploading", progress: 1 } }));
        await putFile(uploadUrl, file, signedContentType, (progress) => {
            setAttachmentStatuses((current) => ({ ...current, [statusKey]: { label: "Uploading", progress } }));
        });
        setAttachmentStatuses((current) => ({ ...current, [statusKey]: { label: "Uploaded", progress: 100 } }));
        setUploadedObjectKeys((current) => ({ ...current, [identity]: objectKey }));
        return objectKey;
    }, [route, uploadedObjectKeys]);

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();
        setSubmitError("");
        setSuccess(null);
        setAttachmentStatuses({});
        if (!validateForm()) return;
        setIsSubmitting(true);
        try {
            const attachmentObjectKeys = await Promise.all(selectedFiles.map(uploadAttachment));
            const title = trimValue(form.title);
            const description = trimValue(form.description);
            if (route === SUBMISSION_ROUTE.ENDPOINT_BUG) {
                await createBugReport({ title, description, affectedEndpointId: selectedEndpointId, attachmentObjectKeys });
            } else if (route === SUBMISSION_ROUTE.PRODUCT_BUG) {
                await createProductBugReport({ title, description, attachmentObjectKeys });
            } else {
                await createFeatureRequest({ title, description, attachmentObjectKeys });
            }
            setSuccess({ message: "Feedback submitted. Thanks for helping us improve Origin." });
            setForm(INITIAL_FORM);
            setAttachments([]);
            setUploadedObjectKeys({});
            if (fileInputRef.current) fileInputRef.current.value = "";
            setErrors({});
        } catch (error) {
            setSubmitError(errorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    }, [form, route, selectedEndpointId, selectedFiles, uploadAttachment, validateForm]);

    // Switching type resets transient form state (errors, banners) and syncs
    // the deep-link. The bug/feature choice lives in the Type dropdown below,
    // not the chrome's TabStrip — the header carries a single static tab so the
    // seam/height match the rest of the admin shell.
    const changeReportType = (type) => {
        setReportType(type);
        syncReportTypeToLocation(type);
        setErrors({});
        setSubmitError("");
        setSuccess(null);
    };
    const headerTabs = [{ id: "feedback", label: "Send Feedback", active: true }];

    return (
        <>
            <header className="shrink-0 overflow-hidden">
                <TabStrip tabs={headerTabs} className="pt-[11px] max-[900px]:pt-[8px]" />
            </header>
            <div className="flex flex-1 min-h-0 min-w-0 flex-col py-[8px]">
                <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] border-y border-[color:var(--line-soft)] py-[6px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
                    Report a bug or request an improvement for the Origin team.
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto pt-[10px]">
                    {success ? (
                        <div className="mb-[10px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] py-[8px] text-[12px] leading-[1.3] text-[color:var(--ink)]">
                            {success.message}
                        </div>
                    ) : null}
                    {submitError ? (
                        <div className="mb-[10px] rounded-[2px] border border-red-400/50 px-[10px] py-[8px] text-[12px] leading-[1.3] text-red-400">
                            {submitError}
                        </div>
                    ) : null}

                    <form onSubmit={handleSubmit} className="grid min-w-0 items-start gap-[14px] [grid-template-columns:minmax(0,1fr)_300px] max-[860px]:grid-cols-1 pb-[14px]">
                        <div className="grid min-w-0 grid-cols-1 content-start gap-[10px]">
                            <label className="grid gap-[4px]">
                                <span className={NOTE_CLASS}>Type</span>
                                <Select
                                    value={reportType}
                                    onChange={changeReportType}
                                    options={TYPE_OPTIONS}
                                    ariaLabel="Feedback type"
                                />
                            </label>
                            <TextInput
                                label="Title"
                                value={form.title}
                                onChange={(e) => updateField("title", e.target.value)}
                                error={errors.title}
                                maxLength={200}
                                placeholder={PLACEHOLDERS[reportType].title}
                            />
                            <TextArea
                                label="Description"
                                value={form.description}
                                onChange={(e) => updateField("description", e.target.value)}
                                error={errors.description}
                                maxLength={5000}
                                rows={8}
                                className="[&_textarea]:min-h-[200px]"
                                placeholder={PLACEHOLDERS[reportType].description}
                            />
                        </div>

                        <div className="grid min-w-0 grid-cols-1 content-start gap-[10px]">
                            {isBug ? (
                                <div className="grid grid-cols-1 gap-[10px] rounded-[2px] border border-[color:var(--line)] px-[10px] py-[10px]">
                                    <span className={EYEBROW_CLASS}>Affected endpoint</span>
                                    <p className="text-[11px] leading-[1.4] text-[color:var(--muted)]">
                                        Optional. Select an endpoint to attach its agent logs and telemetry. Leave this blank for a general product or dashboard issue.
                                    </p>
                                    <EndpointPicker
                                        value={form.affectedEndpointId}
                                        onChange={(id) => updateField("affectedEndpointId", id)}
                                        error={errors.affectedEndpointId}
                                    />
                                    {hasEndpoint ? (
                                        <div className="grid grid-cols-1 gap-[6px] border-t border-[color:var(--line-soft)] pt-[8px] text-[11px] leading-[1.18]">
                                            {[
                                                ["Agent version", metadataSummary.agentVersion, true],
                                                ["Platform", metadataSummary.platform, false],
                                                ["Observed", metadataSummary.observedAt, false],
                                            ].map(([k, v, mono]) => (
                                                <div key={k} className="flex items-center justify-between gap-[10px]">
                                                    <span className="text-[color:var(--muted)]">{k}</span>
                                                    <span className={`min-w-0 truncate text-right text-[color:var(--ink)] ${mono ? "font-mono" : ""}`}>
                                                        {loadingEndpointMetadata ? "Loading…" : v}
                                                    </span>
                                                </div>
                                            ))}
                                            {endpointMetadataError ? (
                                                <div className="text-[11px] text-red-400">{endpointMetadataError}</div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}

                            <div className="grid grid-cols-1 gap-[8px] rounded-[2px] border border-[color:var(--line)] px-[10px] py-[10px]">
                                <div className="flex items-center justify-between gap-[10px]">
                                    <span className={EYEBROW_CLASS}>Attachments</span>
                                    <span className="text-[10px] leading-[1.08] text-[color:var(--muted-soft)]">{selectedFiles.length}/{MAX_ATTACHMENT_COUNT}</span>
                                </div>
                                <div
                                    className="grid grid-cols-1 justify-items-center gap-[6px] rounded-[2px] border border-dashed border-[color:var(--line)] px-[10px] py-[14px] text-center"
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => { e.preventDefault(); appendAttachments(e.dataTransfer.files); }}
                                >
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        multiple
                                        className="sr-only"
                                        onChange={(e) => appendAttachments(e.target.files)}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] py-[4px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] cursor-pointer"
                                    >
                                        Choose files
                                    </button>
                                    <span className={NOTE_CLASS}>Most file types are accepted. Executables are blocked. Maximum {formatBytes(MAX_ATTACHMENT_BYTES)} per file.</span>
                                </div>
                                <FieldError value={errors.attachments} />

                                <AttachmentList
                                    files={selectedFiles}
                                    statuses={attachmentStatuses}
                                    onRemove={removeAttachment}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="inline-flex h-[30px] w-full items-center justify-center rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase font-medium text-[color:var(--ink)] hover:border-[color:var(--green)] disabled:opacity-50 cursor-pointer"
                            >
                                {isSubmitting ? "Submitting…" : selectedType.submitLabel}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
}
